import {el, prefersReducedMotion} from "./progress"

const PROVIDERS = [
  {key: "gmail", label: "Gmail", fill: "fill-glow-azure"},
  {key: "outlook", label: "Outlook", fill: "fill-glow-periwinkle"},
  {key: "yahoo", label: "Yahoo", fill: "fill-glow-orchid"},
  {key: "other", label: "Other", fill: "fill-glow-mint"}
]

const WIDTH = 640
const HEIGHT = 220
const PAD = {l: 8, r: 8, t: 16, b: 32}

function daySchedule(day) {
  if (day <= 2) return 75
  if (day <= 4) return 200
  if (day <= 7) return 400
  if (day <= 10) return 700
  if (day <= 14) return 1250
  if (day <= 17) return 2500
  if (day <= 21) return 4500
  if (day <= 25) return 8750
  return null
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value))
}

function mixValues(root) {
  return PROVIDERS.map((provider) => {
    const slider = root.querySelector(`[data-learn-mix='${provider.key}']`)
    return Number(slider?.value || 0)
  })
}

function renormalise(values, locked) {
  const next = values.map((value, index) => (index === locked ? clamp(Math.round(value), 0, 100) : value))
  const leftover = 100 - next[locked]
  const rest = next.reduce((sum, value, index) => (index === locked ? sum : sum + value), 0)
  let scaled
  if (rest <= 0) {
    const each = Math.floor(leftover / (next.length - 1))
    let rem = leftover - each * (next.length - 1)
    scaled = next.map((value, index) => {
      if (index === locked) return value
      const extra = rem > 0 ? 1 : 0
      rem -= extra
      return each + extra
    })
  } else {
    scaled = next.map((value, index) => (index === locked ? value : (value * leftover) / rest))
  }
  const rounded = scaled.map((value, index) => (index === locked ? next[locked] : Math.round(value)))
  let drift = 100 - rounded.reduce((sum, value) => sum + value, 0)
  for (let i = 0; drift !== 0 && i < rounded.length; i += 1) {
    if (i === locked) continue
    const adj = drift > 0 ? 1 : -1
    if (rounded[i] + adj < 0) continue
    rounded[i] += adj
    drift -= adj
  }
  return rounded
}

function volumesFor(target, mix) {
  return Array.from({length: 30}, (_, index) => {
    const day = index + 1
    const scheduled = daySchedule(day)
    return mix.map((percent) => {
      const cap = Math.round(target * (percent / 100))
      if (scheduled == null) return cap
      return Math.min(scheduled, cap)
    })
  })
}

function fullDay(series, target, mix) {
  const caps = mix.map((percent) => Math.round(target * (percent / 100)))
  const index = series.findIndex((day) => day.every((value, i) => value >= caps[i]))
  return index === -1 ? 30 : index + 1
}

function svgEl(name, attrs) {
  const node = document.createElementNS("http://www.w3.org/2000/svg", name)
  Object.entries(attrs).forEach(([key, value]) => node.setAttribute(key, String(value)))
  return node
}

function drawChart(svg, series, animate) {
  const innerW = WIDTH - PAD.l - PAD.r
  const innerH = HEIGHT - PAD.t - PAD.b
  const gap = 2
  const barW = innerW / 30
  const peak = Math.max(1, ...series.map((day) => day.reduce((sum, value) => sum + value, 0)))
  const group = svg.querySelector("[data-learn-bars]")
  group.replaceChildren()
  series.forEach((day, index) => {
    let y = PAD.t + innerH
    day.forEach((value, provider) => {
      const h = (value / peak) * innerH
      y -= h
      const rect = svgEl("rect", {
        x: PAD.l + index * barW + gap / 2,
        y,
        width: Math.max(barW - gap, 1),
        height: Math.max(h, 0),
        rx: 1,
        class: `${PROVIDERS[provider].fill} ${index < 14 ? "opacity-70" : ""}`
      })
      if (animate) {
        rect.style.transformOrigin = `${PAD.l + index * barW + barW / 2}px ${PAD.t + innerH}px`
        rect.style.transform = "scaleY(0)"
        rect.style.opacity = "0"
        rect.style.transition = `transform 320ms ease-out ${index * 18}ms, opacity 320ms ease-out ${index * 18}ms`
      }
      group.append(rect)
    })
  })
  if (animate) {
    window.requestAnimationFrame(() => {
      group.querySelectorAll("rect").forEach((rect) => {
        rect.style.transform = "scaleY(1)"
        rect.style.opacity = "1"
      })
    })
  }
}

function milestones(series, ready) {
  const picks = [1, 7, 14, ready, 30].filter((day, index, all) => all.indexOf(day) === index).sort((a, b) => a - b)
  return picks.map((day) => {
    const total = series[day - 1].reduce((sum, value) => sum + value, 0)
    return {day, total}
  })
}

function render(root, options) {
  const target = Math.max(1, Number(root.querySelector("[data-learn-target]")?.value) || 10000)
  const mix = mixValues(root)
  const series = volumesFor(target, mix)
  const ready = fullDay(series, target, mix)
  const svg = root.querySelector("[data-learn-chart]")
  if (svg) drawChart(svg, series, Boolean(options?.animate))
  const summary = root.querySelector("[data-learn-full]")
  if (summary) summary.textContent = `Full volume on day ${ready}.`
  const list = root.querySelector("[data-learn-milestones]")
  if (list) {
    const fmt = new Intl.NumberFormat("en-US")
    list.replaceChildren(...milestones(series, ready).map((row) => {
      const item = el("li", "flex items-baseline justify-between gap-3 text-[13px]")
      item.append(el("span", "text-ink-subtle", `Day ${row.day}`), el("span", "tabular-nums text-ink", fmt.format(row.total)))
      return item
    }))
  }
  PROVIDERS.forEach((provider, index) => {
    const label = root.querySelector(`[data-learn-mix-value='${provider.key}']`)
    if (label) label.textContent = `${mix[index]}%`
  })
}

export function mount(root) {
  const sliders = Array.from(root.querySelectorAll("[data-learn-mix]"))
  sliders.forEach((slider, index) => {
    slider.addEventListener("input", () => {
      const next = renormalise(mixValues(root), index)
      sliders.forEach((node, i) => { node.value = String(next[i]) })
      render(root)
    })
  })
  root.querySelector("[data-learn-target]")?.addEventListener("input", () => render(root))
  if (prefersReducedMotion()) return
  const svg = root.querySelector("[data-learn-chart]")
  if (!svg) return
  const observer = new IntersectionObserver((entries) => {
    if (!entries[0]?.isIntersecting) return
    observer.disconnect()
    render(root, {animate: true})
  }, {threshold: 0.25})
  observer.observe(svg)
}
